import type { ReactNode } from 'react';
import { Link } from 'react-router';

/**
 * A ruled list, the way a squad list or a fixture list is actually printed.
 *
 * Not a table element: the same rows need to reflow into two lines on a phone,
 * and a real <table> fights that. Column alignment comes from grid templates the
 * caller supplies, and tabular figures keep the numbers in line.
 */
export function Sheet({ children }: { children: ReactNode }) {
  return <ul className="divide-y divide-line">{children}</ul>;
}

export function SheetRow({
  children,
  onClick,
  to,
  action,
  wrap = false,
  highlight = false,
}: {
  children: ReactNode;
  onClick?: () => void;
  /** Makes the whole row the link, rather than a line of text inside it. */
  to?: string;
  /**
   * Lets the trailing columns drop to a second line when there is no room.
   *
   * A readiness row carries a name, a sentence of signals, a ratio and a
   * status: on a 390px screen those compete, and the name truncates while the
   * sentence wraps to four lines. Wrapping gives the words the width and puts
   * the figures underneath.
   */
  wrap?: boolean;
  /**
   * A second thing the row can do, beside the first.
   *
   * It sits outside the clickable wrapper, because a link inside a button is
   * invalid and a button inside a link is worse. Use it sparingly: a row with
   * two equal actions is a row where neither is obvious.
   */
  action?: ReactNode;
  highlight?: boolean;
}) {
  const interactive = Boolean(onClick || to);
  const body = (
    <div
      className={`flex w-full gap-3 px-4 py-3 text-start transition-colors ${
        wrap ? 'flex-wrap items-baseline' : 'items-center'
      } ${
        highlight ? 'bg-signal-soft' : ''
      } ${interactive ? 'hover:bg-surface-sunken' : ''}`}
    >
      {children}
    </div>
  );
  const wrapped = to ? (
    // The row is the target. A link around the name alone is about 25px tall
    // and sits inside 48px of row that does nothing when tapped, which on a
    // phone reads as a list that only sometimes works.
    <Link to={to} className="block w-full min-w-0">
      {body}
    </Link>
  ) : onClick ? (
    <button type="button" onClick={onClick} className="block w-full min-w-0">
      {body}
    </button>
  ) : (
    body
  );

  if (!action) return <li>{wrapped}</li>;

  return (
    <li className={`flex items-center ${highlight ? 'bg-signal-soft' : ''}`}>
      <div className="min-w-0 flex-1">{wrapped}</div>
      <div className="shrink-0 pe-3">{action}</div>
    </li>
  );
}

/** The leading figure: a shirt number, a match-day offset, a rank. */
export function SheetNumber({ children }: { children: ReactNode }) {
  return (
    <span className="tabular w-8 shrink-0 font-condensed text-subhead font-semibold tabular-nums text-ink-muted">
      {children}
    </span>
  );
}

export function SheetHead({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 border-b border-line bg-surface-sunken px-4 py-2 text-meta text-ink-faint">
      {children}
    </div>
  );
}
